import { Inject, Injectable, InjectionToken } from '@angular/core';
import { APP_CONFIG, IAppConfig } from '../../app.config';
import {
  HogiaIdAssociationPreview,
  HogiaIdAssociationProof,
} from '../../core/helper/hogia-id-association-state';
import { isHogiaId32ByteBase64UrlValue } from '../../core/helper/hogia-id-browser-transaction';
import { isValidSessionExpiresInSeconds } from '../../core/helper/session-storage';

export const HOGIA_ID_ASSOCIATION_TIMEOUT_MS = 10_000;

export type HogiaIdAssociationAuthentication =
  | { grant_type: 'client_credentials'; username: string; password: string }
  | { grant_type: 'bankid_credentials'; order_reference: string };

export interface HogiaIdAssociationPreviewResult {
  session: {
    accessToken: string;
    expiresInSeconds: number;
  };
  preview: HogiaIdAssociationPreview;
}

export type HogiaIdAssociationResult<T> =
  | { ok: true; value: T }
  | { ok: false; status?: number };

interface PreviewWireResponse {
  token?: {
    access_token?: unknown;
    expires_in?: unknown;
  };
  preview?: {
    dhUserName?: unknown;
    organizationName?: unknown;
    hogiaDisplayName?: unknown;
  };
}

export const HOGIA_ID_ASSOCIATION_FETCH = new InjectionToken<typeof fetch>('HOGIA_ID_ASSOCIATION_FETCH', {
  providedIn: 'root',
  factory: () => window.fetch.bind(window),
});

@Injectable({ providedIn: 'root' })
export class HogiaIdAssociationService {
  private readonly endpoint: string;

  constructor(
    @Inject(APP_CONFIG) config: IAppConfig,
    @Inject(HOGIA_ID_ASSOCIATION_FETCH) private readonly fetchImplementation: typeof fetch
  ) {
    this.endpoint = `${config.apiEndpointV2}/identity/hogia-id/association`;
  }

  async preview(
    proof: HogiaIdAssociationProof,
    authentication: HogiaIdAssociationAuthentication,
    signal?: AbortSignal
  ): Promise<HogiaIdAssociationResult<HogiaIdAssociationPreviewResult>> {
    if (!isValidProof(proof)) {
      return { ok: false };
    }

    const response = await this.post(
      'preview',
      {
        associationCode: proof.associationCode,
        code_verifier: proof.codeVerifier,
        authentication,
      },
      undefined,
      signal
    );
    if (response.ok === false) {
      return { ok: false, status: response.status };
    }

    let body: PreviewWireResponse;
    try {
      body = (await response.value.json()) as PreviewWireResponse;
    } catch {
      return { ok: false, status: response.value.status };
    }

    const accessToken = body?.token?.access_token;
    const expiresInSeconds = body?.token?.expires_in;
    const preview = body?.preview;
    if (
      typeof accessToken !== 'string' ||
      !accessToken ||
      !isValidSessionExpiresInSeconds(expiresInSeconds) ||
      typeof preview?.dhUserName !== 'string' ||
      !preview.dhUserName ||
      typeof preview.organizationName !== 'string' ||
      !preview.organizationName ||
      (preview.hogiaDisplayName !== undefined &&
        preview.hogiaDisplayName !== null &&
        typeof preview.hogiaDisplayName !== 'string')
    ) {
      return { ok: false, status: response.value.status };
    }

    return {
      ok: true,
      value: {
        session: { accessToken, expiresInSeconds },
        preview: {
          dhUserName: preview.dhUserName,
          organizationName: preview.organizationName,
          hogiaDisplayName:
            typeof preview.hogiaDisplayName === 'string' && preview.hogiaDisplayName.trim()
              ? preview.hogiaDisplayName
              : undefined,
        },
      },
    };
  }

  async confirm(
    proof: HogiaIdAssociationProof,
    accessToken: string,
    signal?: AbortSignal
  ): Promise<HogiaIdAssociationResult<void>> {
    if (!isValidProof(proof) || !accessToken) {
      return { ok: false };
    }

    const response = await this.post(
      'confirm',
      { associationCode: proof.associationCode, code_verifier: proof.codeVerifier },
      accessToken,
      signal
    );
    if (response.ok === false) {
      return { ok: false, status: response.status };
    }
    if (response.value.status !== 204) {
      return { ok: false, status: response.value.status };
    }
    return { ok: true, value: undefined };
  }

  async cancel(
    proof: HogiaIdAssociationProof,
    signal?: AbortSignal
  ): Promise<HogiaIdAssociationResult<void>> {
    if (!isValidProof(proof)) {
      return { ok: false };
    }

    const response = await this.post(
      'cancel',
      { associationCode: proof.associationCode, code_verifier: proof.codeVerifier },
      undefined,
      signal
    );
    if (response.ok === false) {
      return { ok: false, status: response.status };
    }
    return { ok: true, value: undefined };
  }

  private async post(
    operation: 'preview' | 'confirm' | 'cancel',
    body: object,
    accessToken?: string,
    externalSignal?: AbortSignal
  ): Promise<HogiaIdAssociationResult<Response>> {
    const abortController = new AbortController();
    const abortFromExternal = () => abortController.abort();
    externalSignal?.addEventListener('abort', abortFromExternal, { once: true });
    if (externalSignal?.aborted) {
      abortController.abort();
    }
    const timeoutHandle = window.setTimeout(() => abortController.abort(), HOGIA_ID_ASSOCIATION_TIMEOUT_MS);

    try {
      const headers: Record<string, string> = { 'Content-Type': 'application/json' };
      if (accessToken) {
        headers.Authorization = `Bearer ${accessToken}`;
      }

      const response = await this.fetchImplementation(`${this.endpoint}/${operation}`, {
        method: 'POST',
        headers,
        body: JSON.stringify(body),
        cache: 'no-store',
        credentials: 'omit',
        redirect: 'error',
        signal: abortController.signal,
      });
      return response.ok ? { ok: true, value: response } : { ok: false, status: response.status };
    } catch {
      return { ok: false };
    } finally {
      window.clearTimeout(timeoutHandle);
      externalSignal?.removeEventListener('abort', abortFromExternal);
    }
  }
}

function isValidProof(proof: HogiaIdAssociationProof | undefined): proof is HogiaIdAssociationProof {
  return (
    isHogiaId32ByteBase64UrlValue(proof?.associationCode) &&
    isHogiaId32ByteBase64UrlValue(proof?.codeVerifier)
  );
}
